import React, { useState, useEffect, useContext } from "react";
import { GameContext } from "../../Context/GameContext";
import { useDifficultyRatings } from "../../Context/DifficultyRatingsContext";
import { roomDetails } from "../../Config/config";
import { RoomStatus, RoomDetails } from "../../Types/Game";
import DifficultyIndicator, {
  DIFFICULTY_NAMES,
} from "../DifficultyIndicator/DifficultyIndicator";

/**
 * RoomsIcons
 * ----------
 * Renders the clickable set of room icons on the intro screen.
 *
 * Behavior:
 * - Pulls room state and profile settings from GameContext.
 * - Builds a list of rooms via `roomDetails(roomStates)`.
 * - Applies a CSS class based on each room's status (open/completed/trapped/active).
 * - In "singleRoom" mode, clicking an open room sets it as the current room.
 */

const RoomsIcons: React.FC = () => {
  // Pull context values we need to render and to handle click behavior.
  // The GameContext type should expose these fields; if not, update its typing.
  const {
    setCurrentRoom,
    currentRoom,
    profileSettings,
    roomStates,
    hasLoadedRoomStates,
  } = useContext(GameContext);
  const { ratings: difficultyRatings } = useDifficultyRatings();

  /**
   * Local state: derived room details list (typed).
   * `roomDetails(roomStates)` is assumed to return RoomDetails[].
   */
  const [details, setDetails] = useState<RoomDetails[]>([]);

  /**
   * When roomStates change (e.g., fetched/updated), rebuild the details list.
   */
  useEffect(() => {
    if (!roomStates) return;
    const updatedDetails = roomDetails(roomStates, difficultyRatings);
    setDetails(updatedDetails);
  }, [roomStates, difficultyRatings]);

  /**
   * Compute a CSS class indicating how a room should be styled.
   * - activeRoom: currently selected & open (single-room mode)
   * - completed / trapped: status badges
   * - nonActiveRoom: otherwise clickable in single-room mode
   */
  const getActiveClass = (room: RoomDetails): string => {
    if (!profileSettings?.singleRoom) return "";
    if (currentRoom === room.id && room.status === RoomStatus.Open) return "active-room";
    if (room.status === RoomStatus.Completed) return "completed";
    if (room.status === RoomStatus.Trapped) return "trapped";
    if (!room.status || room.status === RoomStatus.Open) return "non-active-room";
    // Fallback (covers any other/legacy status)
    return "completed";
  };

  /**
   * Click handler for a room icon.
   * In single-room mode, only rooms that are "Open" (or missing status for
   * backwards compatibility) are clickable to set as current.
   */
  const handleRoomClick = (room: RoomDetails) => {
    // Helpful diagnostics while developing
    // console.log("Room clicked:", room);

    if (!profileSettings?.singleRoom) {
      //console.log("Not in single room mode");
      return;
    }

    // console.log("Single room mode, checking status:", room.status, RoomStatus.Open);

    if (!room.status || room.status === RoomStatus.Open) {
      // console.log("Setting current room to:", room.id);
      setCurrentRoom(room.id);
    } else {
      // console.log("Room not clickable, status:", room.status);
    }
  };

  /**
   * Only show rooms that should be visible (`room.state` truthy), ordered by `order`.
   */
  const filteredRooms = details
    .filter((room) => room.state)
    .sort((a, b) => a.order - b.order);

  // Gate rendering until we've loaded room states, to avoid flicker/wrong UI.
  if (!hasLoadedRoomStates) return null;

  return (
    <div className="intro-icons">
      {filteredRooms.map((room) => (
        <div key={room.id} className="icon-container">
          <div
            className={`intro-icon ${room.className} ${getActiveClass(room)}`}
            onClick={() => handleRoomClick(room)}
          >
            {/* Labels arrive as HTML fragments (e.g., include <br/> or icons) */}
            <span dangerouslySetInnerHTML={{ __html: room.label as unknown as string }} />
            {profileSettings?.singleRoom && room.status === RoomStatus.Completed && (
              <span className="icon-with-tick" />
            )}
            {profileSettings?.singleRoom && room.status === RoomStatus.Trapped && (
              <div className="icon-with-slime" />
            )}
          </div>
          {/* Sibling of .intro-icon, not a child — some rooms (e.g. room99)
              recolour their placeholder art via a CSS `filter: hue-rotate(...)`
              on .intro-icon, which composites and shifts the colour of any DOM
              nested inside it too. Keeping the badge outside that box means it
              always shows its true difficulty colours. */}
          {profileSettings?.showDifficultyIndicators !== false && (
            <span
              className="difficulty-badge-wrapper difficulty-tooltip"
              data-tooltip={`Difficulty: ${DIFFICULTY_NAMES[room.difficulty]}`}
              tabIndex={0}
            >
              <DifficultyIndicator
                level={room.difficulty}
                className="difficulty-badge"
              />
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default RoomsIcons;